import { registerService } from './lib';

export type ComplianceRiskLevel =
  | 'SEVERE'
  | 'HIGH'
  | 'MODERATE'
  | 'LOW'
  | 'UNKNOWN';

export type RestrictionType = 'global_ban' | 'withdraw_ban' | 'withdraw_limit';

export type RestrictionLiftType = 'manual' | 'automatic';

export type Restriction = {
  type: RestrictionType;
  value: string | null;
  reason: string;
  start_at: number;
  end_at: number | null;
  is_active: boolean;
  /** Epoch when the restriction actually stopped; `null` while in effect. */
  lifted_at: number | null;
  /** How the restriction ended; `null` while in effect. */
  lift_type: RestrictionLiftType | null;
};

export type RestrictionInput = {
  type: RestrictionType;
  reason: string;
  end_at: number | null;
  value?: string;
};

export type ComplianceUser = {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  kyc_level: 0 | 1 | 2 | 3;
  risk_score: number | null;
  risk_level: ComplianceRiskLevel | null;
  flagged_deposits_count: number;
  flagged_withdraws_count: number;
  screened_transactions_count: number;
  last_flagged_at: number;
  restrictions: Restriction[];
};

export type ComplianceUserDetail = ComplianceUser & {
  restriction_history: Restriction[];
  stats: {
    deposits_count_30d: number;
    withdraws_count_30d: number;
    flagged_count_30d: number;
  };
};

export type ComplianceKytIdentification = {
  category: string;
  name: string;
  description: string;
  url: string;
};

export type ComplianceKytReport = {
  chainalysis?: { identifications: ComplianceKytIdentification[] };
  misttrack?: {
    score?: number;
    risk_level?: string;
    hacking_event?: string;
    address_label?: string;
    detail_list?: string[];
    req_timestamp?: number;
    triangle_level?: [number, number, number]; // eg [1,1,3] [Risky entity, Hacking event, Suspicious txn]
    risk_report_url?: string;
    counterparty_list?: {
      name: string;
      amount: number;
      percent: number;
    }[];
    risk_detail?: {
      entity?: string;
      exposure_type?: string;
      hop_num?: number;
      percent?: number;
      risk_type?: string;
      volume?: number;
      hop_dic?: Record<number, string[]>;
    }[];
  } & Record<string, unknown>;
};

export type ComplianceKytSummary = {
  risk_level: ComplianceRiskLevel | null;
  risk_score: number | null;
  flags: string[];
  report: ComplianceKytReport | null;
};

export type ComplianceDepositStatus = 'PENDING' | 'DONE' | 'FAILED';

export type ComplianceDeposit = {
  trace_id: number;
  tracking_code: string;
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  coin: string;
  network: string;
  amount: string;
  status: ComplianceDepositStatus;
  transaction_type: 'deposit';
  tx_hash: string;
  tx_index: number;
  from_address: string;
  kyt: ComplianceKytSummary;
  created_at: number;
};

export type ComplianceWithdrawStatus =
  | 'CREATED'
  | 'VERIFIED'
  | 'SENT'
  | 'DONE'
  | 'ADMIN_CANCELED'
  | 'FAILED_BEFORE_SENDING';

export type ComplianceWithdraw = {
  trace_id: number;
  tracking_code: string;
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  coin: string;
  network: string;
  amount: string;
  fee: string;
  amount_iqd: string;
  address: string;
  tag: string | null;
  tx_hash: string;
  status: ComplianceWithdrawStatus;
  failure_error: string | null;
  kyt: ComplianceKytSummary;
  created_at: number;
};

/** `usd_value` is `null` until the threshold has been configured. */
export type ComplianceMisttrackThreshold = {
  usd_value: string | null;
};

declare module './lib' {
  interface ServiceRegistry {
    'compliance-panel': ServiceEndpoints<{
      'get:users/': {
        params: {
          risk_level?: ComplianceRiskLevel;
          has_restriction?: boolean;
          restriction_type?: RestrictionType;
          search?: string;
          limit?: number;
        };
        response: ComplianceUser[];
      };
      'get:users/{slug}/': {
        params: { slug: string };
        response: ComplianceUserDetail;
      };
      'get:deposits/': {
        params: {
          user_id?: string;
          risk_level?: ComplianceRiskLevel;
          flagged?: boolean;
          coin?: string;
          network?: string;
          status?: ComplianceDepositStatus;
          search?: string;
          created_after?: number;
          created_before?: number;
          limit?: number;
        };
        response: ComplianceDeposit[];
      };
      'get:withdraws/': {
        params: {
          user_id?: string;
          risk_level?: ComplianceRiskLevel;
          flagged?: boolean;
          coin?: string;
          network?: string;
          status?: ComplianceWithdrawStatus;
          search?: string;
          created_after?: number;
          created_before?: number;
          limit?: number;
        };
        response: ComplianceWithdraw[];
      };
      'get:settings/misttrack-threshold/': {
        response: ComplianceMisttrackThreshold;
      };
      'put:settings/misttrack-threshold/': {
        params: {
          usd_value: string;
        };
        response: ComplianceMisttrackThreshold;
      };
    }>;
  }
}

registerService('compliance-panel', '/proxy/wealth/panel/compliance');
